import { t } from "i18next";
import { match } from "ts-pattern";
import { z } from "zod";

import {
  AdHocPlaceOfSafetySchema,
  CuratedPlaceOfSafetySchema,
} from "@thalamos/common";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  longTextSchema,
  shortTextSchema,
  yesOnlySchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { getSupportedIncidentTypeSelection } from "../../workflowLogic/helpers/getIncidentTypeSelection.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";
import { includeOnlyEmergencyDepartmentForMca } from "../common/placesOfSafetyFilter.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "placeOfSafety",
    type: "placeOfSafety",
    schema: z.union([CuratedPlaceOfSafetySchema, AdHocPlaceOfSafetySchema]),
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("recordPlaceOfSafety.choosePlaceOfSafety.title"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    filter: includeOnlyEmergencyDepartmentForMca,
  })
  .withFieldAlwaysVisible({
    fieldName: "confirmPlaceOfSafetyCorrect",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("recordPlaceOfSafety.choosePlaceOfSafety.confirmCorrect"),
    isEditable: ({ formValues }) => formValues.placeOfSafety !== undefined,
  })
  .withFieldConditionallyVisible({
    fieldName: "placeOfSafetyType",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "hbPos136" as const,
          "emergencyDepartment" as const,
          "returnedToMentalHealthUnit" as const,
          "careHome" as const,
          "custody" as const,
          "privateHome" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.hbPos136",
        ),
        value: "hbPos136",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.emergencyDepartment",
        ),
        value: "emergencyDepartment",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.returnedToMentalHealthUnit",
        ),
        value: "returnedToMentalHealthUnit",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.careHome",
        ),
        value: "careHome",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.custody",
        ),
        value: "custody",
      },
      {
        label: tk(
          "recordPlaceOfSafety.choosePlaceOfSafetyType.options.privateHome",
        ),
        value: "privateHome",
      },
      {
        label: tk("recordPlaceOfSafety.choosePlaceOfSafetyType.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      match(
        incidentWorkItem
          ? getSupportedIncidentTypeSelection({ incidentWorkItem })
              // .orTee((e) => {
              //   console.error("Unsupported incident type", e.prettifyError());
              // })
              .unwrapOr(undefined)
          : undefined,
      )
        .with("136", () => true)
        .with("mca", () => false)
        .with(undefined, () => true)
        .exhaustive(),
    label: tk("recordPlaceOfSafety.choosePlaceOfSafetyType.title"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "publicPlaceLocationDescription",
    type: "banner",
    bannerType: "warning",
    isVisible: ({ formValues }) =>
      formValues.placeOfSafetyType?.value === "privateHome",
    body: tk(
      "recordPlaceOfSafety.choosePlaceOfSafetyType.publicPlaceLocationDescriptionBanner",
    ),
  })
  .withFieldConditionallyVisible({
    fieldName: "placeOfSafetyTypeOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.placeOfSafetyType?.value === "other",
    label: tk("recordPlaceOfSafety.choosePlaceOfSafetyTypeOther.title"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whoAuthorisedCustodySuite",
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.placeOfSafetyType?.value === "custody",
    label: tk(
      "recordPlaceOfSafety.choosePlaceOfSafetyType.whoAuthorisedCustodySuite",
    ),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "custodyActionBanner",
    type: "banner",
    bannerType: "warning",
    body: tk("recordPlaceOfSafety.choosePlaceOfSafetyType.warningBanner"),
    isVisible: ({ formValues }) =>
      match(formValues.placeOfSafetyType?.value)
        .with("custody", () => true)
        .with(
          "hbPos136",
          "emergencyDepartment",
          "privateHome",
          "returnedToMentalHealthUnit",
          "careHome",
          "other",
          () => false,
        )
        .with(undefined, () => false)
        .exhaustive(),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyNotHbPosReason",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "noS136SuitesAvailable" as const,
          "emergencyMedicalNeed" as const,
          "substanceRelatedEmergency" as const,
          "secureCustodyRequired" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "recordPlaceOfSafety.whyNotHbPosReason.options.noS136SuitesAvailable",
        ),
        value: "noS136SuitesAvailable",
      },
      {
        label: tk(
          "recordPlaceOfSafety.whyNotHbPosReason.options.emergencyMedicalNeed",
        ),
        value: "emergencyMedicalNeed",
      },
      {
        label: tk(
          "recordPlaceOfSafety.whyNotHbPosReason.options.substanceRelatedEmergency",
        ),
        value: "substanceRelatedEmergency",
      },
      {
        label: tk(
          "recordPlaceOfSafety.whyNotHbPosReason.options.secureCustodyRequired",
        ),
        value: "secureCustodyRequired",
      },
      {
        label: tk("recordPlaceOfSafety.whyNotHbPosReason.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      match(formValues.placeOfSafetyType?.value)
        .with("hbPos136", undefined, () => false)
        .with(
          "other",
          "emergencyDepartment",
          "returnedToMentalHealthUnit",
          "careHome",
          "custody",
          "privateHome",
          () => true,
        )
        .exhaustive(),
    label: tk("recordPlaceOfSafety.whyNotHbPosReason.title"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyNotHbPosReasonOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      match(formValues.whyNotHbPosReason?.value)
        .with("other", () => true)
        .otherwise(() => false),
    label: tk("recordPlaceOfSafety.whyNotHbPosReasonOther.title"),
    multiline: true,
    rows: "5",
  })
  .withPage(["placeOfSafety", "confirmPlaceOfSafetyCorrect"])
  .withPage([
    "placeOfSafetyType",
    "placeOfSafetyTypeOther",
    "whoAuthorisedCustodySuite",
    "custodyActionBanner",
    "publicPlaceLocationDescription",
  ])
  .withPage(["whyNotHbPosReason", "whyNotHbPosReasonOther"])
  .build({ t });

export type RecordPlaceOfSafetyFormData = ReturnType<
  typeof formSpecification
>["formType"];
